import React, { useState, useEffect } from 'react'
import { Table, Button, Icon, Tooltip } from 'antd'
import moment from 'moment'
import { Map, CircleMarker, Popup, TileLayer, GeoJSON, Marker } from 'react-leaflet'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import { geojsonIndia } from '../../util/methods'
import GeoJSONLabels from '../../components/GeoJSONLabels/GeoJSONLabels'
import { getLeafletTileConfig } from '../../util/mapTiles'

function IdleTimeModal({ idleTimeData = [], hasFuelSensorAttached = false, idleConsumptionRate = null, fuelRate = null }) {
    const tileLayerConfig = getLeafletTileConfig()

    // A rate/value is considered "defined" only when it is a finite, positive number
    const isDefined = (v) => v !== null && v !== undefined && v !== '' && Number.isFinite(Number(v)) && Number(v) > 0

    // Renders "NA" with the exact reason shown directly in a tiny font, plus an (i) tooltip on hover
    const renderNA = (message) => (
        <span>
            NA{' '}
            <Tooltip title={message} overlayStyle={{ zIndex: 1200 }}>
                <Icon type="info-circle" style={{ color: '#faad14', cursor: 'pointer' }} />
            </Tooltip>
            <div style={{ fontSize: 10, lineHeight: 1.2, color: '#8c8c8c', marginTop: 2 }}>{message}</div>
        </span>
    )

    // Estimated fuel consumed (Ltrs) = Idle Consumption Rate (Ltrs/Hr) * Idle Duration (Hr)
    const getEstimatedFuelConsumed = (record) => {
        if (!isDefined(idleConsumptionRate)) return null
        const durationHr = (Number(record && record.duration) || 0) / (1000 * 3600)
        return Number(idleConsumptionRate) * durationHr
    }
    const [isMapView, setIsMapView] = useState(false)
    const [isSingleMapView, setIsSingleMapView] = useState(false)
    const [selectedRecord, setSelectedRecord] = useState(null)
    const [zoom, setZoom] = useState(18);

    // Handle zoom change
    const handleZoomChange = () => {
        // Get the map instance and update zoom state
        const mapElement = document.querySelector('.leaflet-container');
        if (mapElement && mapElement._leaflet_map) {
            setZoom(mapElement._leaflet_map.getZoom());
        }
    };

    const columns = [
        {
            title: 'Start Time',
            dataIndex: 'startTime',
            key: 'startTime',
            sorter: (a, b) => moment(a.startTime).unix() - moment(b.startTime).unix(),
            render: (text) => moment(text).format('DD/MM/YYYY HH:mm:ss')
        },
        {
            title: 'End Time',
            dataIndex: 'endTime',
            key: 'endTime',
            sorter: (a, b) => moment(a.endTime).unix() - moment(b.endTime).unix(),
            render: (text) => moment(text).format('DD/MM/YYYY HH:mm:ss')
        },
        {
            title: 'Location',
            dataIndex: 'location',
            key: 'location',
            sorter: (a, b) => a.location.localeCompare(b.location),
            render: (text, record) => (
                <Icon
                    type="environment"
                    style={{ fontSize: '16px', cursor: 'pointer', color: '#1890ff' }}
                    onClick={() => {
                        setSelectedRecord(record)
                        setIsMapView(true)
                        setIsSingleMapView(true)
                    }}
                />
            )
        },
        {
            title: 'Duration',
            dataIndex: 'duration',
            key: 'duration',
            sorter: (a, b) => a.duration - b.duration,
            render: (text) => {
                const totalSeconds = Math.floor(text / 1000)
                const hours = Math.floor(totalSeconds / 3600)
                const minutes = Math.floor((totalSeconds % 3600) / 60)
                const seconds = totalSeconds % 60
                return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
            }
        },
        {
            title: 'Change in Fuel Level',
            key: 'change_in_fuel_level',
            sorter: (a, b) => {
                const changeA = (Number(a.end_fuel_level) || 0) - (Number(a.start_fuel_level) || 0)
                const changeB = (Number(b.end_fuel_level) || 0) - (Number(b.start_fuel_level) || 0)
                return changeA - changeB
            },
            render: (text, record) => {
                if (!hasFuelSensorAttached) return renderNA('Fuel Sensor not installed')
                const change = (Number(record.end_fuel_level) || 0) - (Number(record.start_fuel_level) || 0)
                return `${change.toFixed(2)} Ltrs`
            }
        },
        {
            title: 'Estimated Fuel Consumed',
            key: 'estimated_fuel_consumed',
            sorter: (a, b) => (getEstimatedFuelConsumed(a) || 0) - (getEstimatedFuelConsumed(b) || 0),
            render: (text, record) => {
                const estimated = getEstimatedFuelConsumed(record)
                if (estimated === null) return renderNA('Idle Consumption Rate not Defined')
                return `${estimated.toFixed(2)} Ltrs`
            }
        },
        {
            title: (
                <span>
                    <Tooltip title="Estimated monetary loss (real money value)">
                        <span
                            role="img"
                            aria-label="rupee coin"
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: 18,
                                height: 18,
                                marginRight: 6,
                                borderRadius: '50%',
                                background: '#faad14',
                                color: '#fff',
                                fontSize: 11,
                                fontWeight: 700,
                                lineHeight: 1,
                            }}
                        >{'₹'}</span>
                    </Tooltip>
                    Estimated Loss
                </span>
            ),
            key: 'estimate_loss',
            sorter: (a, b) => {
                const lossA = isDefined(fuelRate) ? (getEstimatedFuelConsumed(a) || 0) * Number(fuelRate) : 0
                const lossB = isDefined(fuelRate) ? (getEstimatedFuelConsumed(b) || 0) * Number(fuelRate) : 0
                return lossA - lossB
            },
            render: (text, record) => {
                if (!isDefined(fuelRate)) return renderNA('Fuel Rate not defined')
                const estimated = getEstimatedFuelConsumed(record)
                if (estimated === null) return renderNA('Idle Consumption Rate not Defined')
                return `${(estimated * Number(fuelRate)).toFixed(2)}`
            }
        }
    ]

    const getCircleProps = (duration) => {
        // Find the maximum duration from all idle times
        const maxDuration = Math.max(...idleTimeData.map(item => item.duration))

        // Calculate radius: between 100m to 1000m based on duration
        const radius = (duration / maxDuration) * 25 + 5

        // Calculate opacity: between 0.3 to 0.8 based on duration
        const opacity = (duration / maxDuration) * 0.5 + 0.3

        return { radius, opacity }
    }

    const renderMap = () => (
        <div style={{ height: '600px' }}>
            <Map
                center={selectedRecord ? selectedRecord.location.split(',') : idleTimeData[0].location.split(',')}
                zoom={selectedRecord ? 13 : 18}
                maxZoom={tileLayerConfig.maxZoom || 18}
                attributionControl={false}
                style={{ height: '100%' }}
                onZoomend={handleZoomChange}
            >
                <TileLayer
                    url={tileLayerConfig.url}
                    subdomains={tileLayerConfig.subdomains}
                />
                <GeoJSON data={geojsonIndia} style={{ color: 'grey', weight: 1 }} />

                {/* Use common GeoJSONLabels component - handles both polygons and labels */}
                <GeoJSONLabels
                    zoom={zoom}
                    minZoomLevel={17}
                />

                {(selectedRecord ? [selectedRecord] : idleTimeData).map((item) => {
                    const [lat, lng] = item.location.split(',')
                    const { radius, opacity } = getCircleProps(item.duration)

                    if (isSingleMapView) {
                        return (
                            <Marker
                                key={`${item.startTime}-${item.endTime}`}
                                position={[lat, lng]}
                                icon={L.icon({
                                    iconUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png',
                                    iconSize: [25, 41],
                                    iconAnchor: [12, 41]
                                })}
                            >
                                <Popup>
                                    <div>
                                        <strong>Duration: </strong>
                                        {moment.duration(item.duration).humanize()}<br />
                                        <strong>Start: </strong>
                                        {moment(item.startTime).format('DD/MM/YYYY HH:mm:ss')}<br />
                                        <strong>End: </strong>
                                        {moment(item.endTime).format('DD/MM/YYYY HH:mm:ss')}
                                    </div>
                                </Popup>
                            </Marker>
                        )
                    }

                    return (
                        <CircleMarker
                            key={`${item.startTime}-${item.endTime}`}
                            center={[lat, lng]}
                            radius={radius}
                            fillColor="#EE3711"
                            fillOpacity={opacity}
                            color="#B34029"
                            weight={1}
                        >
                            <Popup>
                                <div>
                                    <strong>Duration: </strong>
                                    {moment.duration(item.duration).humanize()}<br />
                                    <strong>Start: </strong>
                                    {moment(item.startTime).format('DD/MM/YYYY HH:mm:ss')}<br />
                                    <strong>End: </strong>
                                    {moment(item.endTime).format('DD/MM/YYYY HH:mm:ss')}
                                </div>
                            </Popup>
                        </CircleMarker>
                    )
                })}
            </Map>
        </div>
    )

    const renderTable = () => (
        <Table
            dataSource={[...idleTimeData].sort((a, b) => b.duration - a.duration)}
            columns={columns}
            pagination={{ pageSize: 10 }}
            rowKey={(record) => `${record.startTime}-${record.endTime}`}
        />
    )

    const calculateTotalDuration = () => {
        const totalMs = idleTimeData.reduce((sum, item) => sum + item.duration, 0)
        const totalSeconds = Math.floor(totalMs / 1000)
        const hours = Math.floor(totalSeconds / 3600)
        const minutes = Math.floor((totalSeconds % 3600) / 60)
        const seconds = totalSeconds % 60
        return `Total Duration: ${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
    }

    return (
        <>
            {isMapView ? renderMap() : renderTable()}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                <Button
                    style={{ marginTop: '10px' }}
                    type="primary"
                    onClick={() => {
                        setIsMapView(!isMapView)
                        setSelectedRecord(null) // Reset selected record when switching views
                        setIsSingleMapView(false)
                    }}
                    size="small"
                >
                    Switch to {isMapView ? 'Table' : 'Map'} View
                </Button>
                <div style={{ fontWeight: 'bold' }}>
                    {calculateTotalDuration()}
                </div>
            </div>
        </>
    )
}

export default IdleTimeModal
